import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import heroImage from '../assets/healthcare-hero.jpg';
import FAQSection from './FAQSection';
import ConsultationCTA from '../components/ConsultationCTA';

const fadeIn = {
  hidden: { opacity: 0, y: 50 },
  visible: (i = 1) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.2, duration: 0.8, ease: 'easeOut' }
  })
};

const HealthcareAI = () => {
  return (
    <>
      <Helmet>
        <title>AI Healthcare Revenue Cycle Management | MDeRCM</title>
        <meta name="description" content="AI-powered healthcare RCM services. Eligibility checks, prior auth, denial management and payment posting. Reduce denials 75%, cut costs 40%." />
        <link rel="canonical" href="https://mdercm.com/healthcare/ai/" />
        <meta property="og:title" content="AI Healthcare Revenue Cycle Management | MDeRCM" />
        <meta property="og:description" content="AI-powered healthcare RCM. Eligibility, prior auth, denial management and payment posting. Reduce denials 75%." />
        <meta property="og:url" content="https://mdercm.com/healthcare/ai/" />
      </Helmet>
      {/* SEO-OPTIMIZED HERO SECTION */}
      <motion.div
        initial="hidden"
        animate="visible"
        variants={fadeIn}
        style={{
          backgroundImage: `linear-gradient(to right, rgba(0, 0, 0, 0.9), rgba(0, 0, 0, 0.6)), url(${heroImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          padding: '80px 5%',
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          alignItems: 'center',
          color: '#ffffff',
        }}
      >
        <div style={{ flex: '1 1 100%', maxWidth: '800px', zIndex: 2 }}>
          <motion.h1 style={{ 
            fontSize: 'clamp(28px, 5vw, 48px)', 
            fontWeight: 700, 
            marginBottom: '24px', 
            lineHeight: 1.3 
          }}>
            Healthcare Revenue Cycle Management Services That Transform Your Bottom Line
          </motion.h1>
          <motion.p style={{ 
            fontSize: 'clamp(16px, 2vw, 20px)', 
            marginBottom: '20px', 
            color: '#dddddd',
            lineHeight: 1.6
          }}>
            Leading medical revenue cycle management services that combine AI automation with expert oversight. MDeRCM delivers comprehensive revenue cycle management consulting services to hospitals, clinics, and healthcare providers across the USA.
          </motion.p>
          <motion.p style={{ 
            fontSize: 'clamp(14px, 2vw, 18px)', 
            marginBottom: '30px', 
            color: '#cccccc',
            lineHeight: 1.6
          }}>
            From medical billing outsourcing services to hospital revenue cycle management, we're among the best revenue cycle management companies delivering measurable results.
          </motion.p>

          <div style={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: '20px', 
            marginBottom: '40px' 
          }}>
            <motion.a
              href="/free-trial"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              style={{
                backgroundColor: '#F26522',
                color: '#ffffff',
                padding: '16px 32px',
                borderRadius: '8px',
                fontSize: '18px',
                fontWeight: '600',
                textDecoration: 'none',
                display: 'inline-block',
                boxShadow: '0 4px 12px rgba(242, 101, 34, 0.3)'
              }}
            >
              Start Free Trial
            </motion.a>
            <motion.a
              href="#services"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              style={{
                backgroundColor: 'transparent',
                color: '#ffffff',
                padding: '16px 32px',
                borderRadius: '8px',
                fontSize: '18px',
                fontWeight: '600',
                textDecoration: 'none',
                display: 'inline-block',
                border: '2px solid #ffffff'
              }}
            >
              Explore Services
            </motion.a>
          </div>

          {/* KEY METRICS */}
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'flex-start',
            gap: '40px',
            marginTop: '40px'
          }}>
            {[
              ['40%', 'Reduction in AR Days'],
              ['98%', 'First-Pass Claim Rate'],
              ['$2M+', 'Average Revenue Recovery']
            ].map(([stat, label], i) => (
              <motion.div
                key={i}
                custom={i}
                variants={fadeIn}
                style={{
                  flex: '1 1 140px',
                  minWidth: '140px',
                  maxWidth: '200px'
                }}
              >
                <div style={{ fontSize: '42px', fontWeight: '700', color: '#F26522', marginBottom: '8px' }}>{stat}</div>
                <div style={{ fontSize: '16px', color: '#ffffff', lineHeight: 1.3 }}>{label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* WHY CHOOSE OUR RCM SERVICES */}
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={fadeIn}
        style={{ padding: '80px 5%', backgroundColor: '#ffffff' }}
      >
        <h1 style={{ fontSize: "clamp(1.8rem, 3.5vw, 2.5rem)", fontWeight: "700", marginBottom: "20px", color: "#ffffff", lineHeight: "1.3" }}>AI-Powered Healthcare Revenue Cycle Management</h1>
          <h2 style={{
          textAlign: 'center',
          fontSize: 'clamp(28px, 4vw, 42px)',
          fontWeight: '700',
          color: '#001F2B',
          marginBottom: '20px'
        }}>
          Why Choose MDeRCM for Outsourced Revenue Cycle Management Services?
        </h2>
        <p style={{
          textAlign: 'center',
          fontSize: '18px',
          color: '#555',
          maxWidth: '900px',
          margin: '0 auto 60px',
          lineHeight: 1.7
        }}>
          As one of the leading revenue cycle management services companies in the USA, we combine cutting-edge AI technology with healthcare expertise to deliver superior results. Our medical billing outsourcing services help you focus on patient care while we optimize your revenue.
        </p>

        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          gap: '30px'
        }}>
          {[
            {
              icon: '🎯',
              title: 'End-to-End RCM Solutions',
              description: 'Complete healthcare revenue cycle management services from patient intake to payment posting, denial management, and accounts receivable optimization.'
            },
            {
              icon: '🤖',
              title: 'AI-Powered Automation',
              description: 'Our revenue cycle management consulting services leverage AI agents to reduce manual work by 80% and accelerate cash flow with intelligent automation.'
            },
            {
              icon: '💰',
              title: 'Healthcare Cost Reduction',
              description: 'Proven healthcare cost reduction strategies that cut operational expenses by 30-50% while improving collection rates and revenue performance.'
            },
            {
              icon: '🏥',
              title: 'Specialized Hospital RCM',
              description: 'Dedicated revenue cycle management services for hospitals with complex billing needs, multi-specialty coordination, and high claim volumes.'
            },
            {
              icon: '📊',
              title: 'Performance Analytics',
              description: 'Real-time dashboards and comprehensive reporting show exactly how to improve revenue cycle management with actionable insights and KPIs.'
            },
            {
              icon: '✅',
              title: 'Compliance Guaranteed',
              description: 'HIPAA-compliant medical billing outsourcing companies certified in all major payer requirements and regulatory standards across the USA.'
            }
          ].map((item, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1, duration: 0.6 }}
              style={{
                width: '100%',
                maxWidth: '380px',
                backgroundColor: '#f9fafb',
                padding: '32px',
                borderRadius: '16px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                border: '1px solid #e5e7eb'
              }}
            >
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>{item.icon}</div>
              <h3 style={{ fontSize: '22px', fontWeight: '700', marginBottom: '12px', color: '#001F2B' }}>
                {item.title}
              </h3>
              <p style={{ fontSize: '16px', color: '#555', lineHeight: '1.7' }}>
                {item.description}
              </p>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* COMPREHENSIVE SERVICES SECTION */}
      <div id="services" style={{ padding: '80px 5%', backgroundColor: '#f7f9fb' }}>
        <motion.h2
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          style={{
            textAlign: 'center',
            fontSize: 'clamp(28px, 4vw, 42px)',
            fontWeight: '800',
            color: '#001F2B',
            marginBottom: '24px'
          }}
        >
          Complete Medical Revenue Cycle Management Services
        </motion.h2>
        <p style={{
          textAlign: 'center',
          fontSize: '18px',
          color: '#555',
          maxWidth: '800px',
          margin: '0 auto 60px',
          lineHeight: 1.7
        }}>
          Our medical billing and coding outsourcing solutions cover every aspect of the revenue cycle, helping healthcare providers maximize reimbursements and minimize administrative burden.
        </p>

        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          gap: '24px'
        }}>
          {[
            {
              icon: '✅',
              title: 'Insurance Eligibility Verification',
              description: 'Real-time verification of patient insurance coverage and benefits before care begins, reducing claim denials by 80%.',
              link: '/healthcare/ai/eligibility-check'
            },
            {
              icon: '📋',
              title: 'Prior Authorization Services',
              description: 'Automated pre-approval process that accelerates authorizations and eliminates back-and-forth delays with payers.',
              link: '/healthcare/ai/prior-authorization'
            },
            {
              icon: '💳',
              title: 'Medical Billing Outsourcing',
              description: 'Expert medical billing outsourcing services ensuring accurate coding, timely submission, and maximum reimbursement rates.',
              link: '/healthcare/ai'
            },
            {
              icon: '📄',
              title: 'Claims Management',
              description: 'AI-powered claims processing that pre-screens for errors and optimizes submissions for first-pass approval rates of 98%+.',
              link: '/healthcare/ai'
            },
            {
              icon: '🚫',
              title: 'Denial Management',
              description: 'Proactive denial prevention and rapid appeals processing to recover revenue and reduce rejection rates by 60%.',
              link: '/healthcare/ai/denial-management'
            },
            {
              icon: '💰',
              title: 'Payment Posting & Reconciliation',
              description: 'Automated payment posting with zero-touch EOB reconciliation that eliminates manual entry and reduces errors.',
              link: '/healthcare/ai/payment-posting'
            },
            {
              icon: '📊',
              title: 'Accounts Receivable Management',
              description: 'Strategic AR follow-up with intelligent automation that reduces aging balances and accelerates cash collections.',
              link: '/healthcare/ai/accounts-receivable'
            },
            {
              icon: '🧑‍⚕️',
              title: 'Patient Intake & Registration',
              description: 'Streamlined patient onboarding that captures clean, complete data for faster processing and improved patient experience.',
              link: '/healthcare/ai/patient-intake'
            },
            {
              icon: '🛡️',
              title: 'Compliance & Credentialing',
              description: 'Continuous monitoring of provider credentials and regulatory compliance to prevent audit issues and ensure payer acceptance.',
              link: '/healthcare/ai/compliance-agent'
            },
            {
              icon: '💵',
              title: 'Contract Management',
              description: 'Insurance contract repricing and analysis to ensure you receive accurate reimbursements per contracted rates.',
              link: '/healthcare/ai/InsuranceContract-Repricing'
            }
          ].map((service, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08, duration: 0.6 }}
              whileHover={{ y: -8, boxShadow: '0 16px 40px rgba(0,0,0,0.12)' }}
              style={{
                width: '100%',
                maxWidth: '360px',
                backgroundColor: '#ffffff',
                padding: '28px',
                borderRadius: '16px',
                boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                textAlign: 'left',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
            >
              <div style={{ fontSize: '40px', marginBottom: '16px' }}>{service.icon}</div>
              <h4 style={{ 
                fontSize: '20px', 
                fontWeight: '700', 
                marginBottom: '12px', 
                color: '#001F2B' 
              }}>
                {service.title}
              </h4>
              <p style={{ 
                fontSize: '15px', 
                color: '#555', 
                lineHeight: '1.6', 
                marginBottom: '16px' 
              }}>
                {service.description}
              </p>
              <Link 
                to={service.link}
                style={{
                  color: '#F26522',
                  fontSize: '15px',
                  fontWeight: '600',
                  textDecoration: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                Learn More →
              </Link>
            </motion.div>
          ))}
        </div>
      </div>

      {/* HOW TO IMPROVE RCM SECTION */}
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={fadeIn}
        style={{ padding: '80px 5%', backgroundColor: '#ffffff' }}
      >
        <h2 style={{
          textAlign: 'center',
          fontSize: 'clamp(28px, 4vw, 42px)',
          fontWeight: '700',
          color: '#001F2B',
          marginBottom: '20px'
        }}>
          How to Improve Revenue Cycle Management: Our Proven Process
        </h2>
        <p style={{
          textAlign: 'center',
          fontSize: '18px',
          color: '#555',
          maxWidth: '900px',
          margin: '0 auto 60px',
          lineHeight: 1.7
        }}>
          Our revenue cycle management consulting services follow a structured approach to identify bottlenecks, implement healthcare cost reduction strategies, and deliver sustainable improvements.
        </p>

        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '32px',
          maxWidth: '1000px',
          margin: '0 auto'
        }}>
          {[
            {
              step: '01',
              title: 'Comprehensive RCM Assessment',
              description: 'We analyze your current revenue cycle performance, identify leakage points, and benchmark against best revenue cycle management companies to establish improvement targets.'
            },
            {
              step: '02',
              title: 'Custom Strategy Development',
              description: 'Our revenue cycle management consulting services create tailored healthcare cost reduction strategies based on your specialty, payer mix, and operational challenges.'
            },
            {
              step: '03',
              title: 'Technology Implementation',
              description: 'Deploy AI-powered automation across eligibility verification, claims processing, denial management, and AR follow-up for immediate efficiency gains.'
            },
            {
              step: '04',
              title: 'Team Training & Integration',
              description: 'Seamless integration with your existing workflows, EHR systems, and practice management software with comprehensive staff training and support.'
            },
            {
              step: '05',
              title: 'Continuous Optimization',
              description: 'Ongoing monitoring, performance analytics, and process refinement to ensure sustained improvements in collection rates and operational efficiency.'
            }
          ].map((item, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.15, duration: 0.6 }}
              style={{
                display: 'flex',
                gap: '24px',
                alignItems: 'flex-start',
                padding: '28px',
                backgroundColor: '#f9fafb',
                borderRadius: '12px',
                border: '2px solid #e5e7eb'
              }}
            >
              <div style={{
                fontSize: '32px',
                fontWeight: '800',
                color: '#F26522',
                minWidth: '60px'
              }}>
                {item.step}
              </div>
              <div>
                <h3 style={{
                  fontSize: '22px',
                  fontWeight: '700',
                  color: '#001F2B',
                  marginBottom: '10px'
                }}>
                  {item.title}
                </h3>
                <p style={{
                  fontSize: '16px',
                  color: '#555',
                  lineHeight: '1.7'
                }}>
                  {item.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* INDUSTRY SPECIALIZATIONS */}
      <div style={{ padding: '80px 5%', backgroundColor: '#001F2B', color: '#ffffff' }}>
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          style={{
            textAlign: 'center',
            fontSize: 'clamp(28px, 4vw, 42px)',
            fontWeight: '800',
            marginBottom: '24px'
          }}
        >
          Specialized Healthcare Revenue Cycle Management Services
        </motion.h2>
        <p style={{
          textAlign: 'center',
          fontSize: '18px',
          color: '#cccccc',
          maxWidth: '800px',
          margin: '0 auto 60px',
          lineHeight: 1.7
        }}>
          Our medical revenue cycle management services are tailored to meet the unique needs of different healthcare specialties and practice types across the USA.
        </p>

        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          gap: '24px'
        }}>
          {[
            {
              icon: '🏥',
              title: 'Hospital Revenue Cycle Management',
              description: 'Comprehensive hospital revenue cycle management for multi-specialty facilities, emergency departments, and inpatient services.',
              link: '/healthcare/ai'
            },
            {
              icon: '🦷',
              title: 'Dental Practice RCM',
              description: 'Specialized dental revenue cycle management with insurance verification, treatment plan optimization, and patient financing.',
              link: '/Dental'
            },
            {
              icon: '🧠',
              title: 'Mental Health & Behavioral Health',
              description: 'Mental health billing expertise including therapy sessions, psychiatric evaluations, and substance abuse treatment coding.',
              link: '/MentalHealth'
            },
            {
              icon: '👨‍⚕️',
              title: 'Primary Care & Family Medicine',
              description: 'Revenue cycle solutions for primary care with preventive care billing, chronic care management, and quality measure reporting.'
            },
            {
              icon: '🏃',
              title: 'Physical Therapy & Rehabilitation',
              description: 'PT-specific billing including functional limitation reporting, Medicare therapy caps, and outcome-based payment models.'
            },
            {
              icon: '💉',
              title: 'Specialty Practices',
              description: 'Customized RCM for cardiology, orthopedics, dermatology, OB/GYN, and other specialty practices with complex coding requirements.'
            }
          ].map((specialty, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1, duration: 0.5 }}
              style={{
                width: '100%',
                maxWidth: '360px',
                backgroundColor: '#ffffff',
                color: '#001F2B',
                padding: '28px',
                borderRadius: '16px',
                textAlign: 'center'
              }}
            >
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>{specialty.icon}</div>
              <h4 style={{ 
                fontSize: '20px', 
                fontWeight: '700', 
                marginBottom: '12px' 
              }}>
                {specialty.title}
              </h4>
              <p style={{ 
                fontSize: '15px', 
                color: '#555', 
                lineHeight: '1.6',
                marginBottom: '16px'
              }}>
                {specialty.description}
              </p>
              {specialty.link && (
                <Link 
                  to={specialty.link}
                  style={{
                    color: '#F26522',
                    fontSize: '15px',
                    fontWeight: '600',
                    textDecoration: 'none'
                  }}
                >
                  Learn More →
                </Link>
              )}
            </motion.div>
          ))}
        </div>
      </div>

      {/* HEALTHCARE COST REDUCTION STRATEGIES */}
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={fadeIn}
        style={{ padding: '80px 5%', backgroundColor: '#ffffff' }}
      >
        <h2 style={{
          textAlign: 'center',
          fontSize: 'clamp(28px, 4vw, 42px)',
          fontWeight: '700',
          color: '#001F2B',
          marginBottom: '20px'
        }}>
          Healthcare Cost Reduction Strategies That Work
        </h2>
        <p style={{
          textAlign: 'center',
          fontSize: '18px',
          color: '#555',
          maxWidth: '900px',
          margin: '0 auto 60px',
          lineHeight: 1.7
        }}>
          Our outsourced revenue cycle management services implement proven healthcare cost reduction consulting strategies that cut expenses while improving revenue performance.
        </p>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '32px',
          maxWidth: '1200px',
          margin: '0 auto'
        }}>
          {[
            {
              strategy: 'Automation-First Approach',
              savings: 'Reduce labor costs by 40-50%',
              description: 'AI-powered automation eliminates repetitive manual tasks in eligibility checks, claims processing, and payment posting.'
            },
            {
              strategy: 'Denial Prevention',
              savings: 'Save $25-50 per prevented denial',
              description: 'Proactive error detection and payer-specific rule validation prevent denials before claims are submitted.'
            },
            {
              strategy: 'AR Optimization',
              savings: 'Reduce collection costs by 35%',
              description: 'Intelligent prioritization and automated follow-up reduce the cost per collection while improving recovery rates.'
            },
            {
              strategy: 'Clean Claim Rate Improvement',
              savings: 'Increase revenue by 15-25%',
              description: 'Higher first-pass approval rates mean faster payments and reduced administrative costs for rework.'
            },
            {
              strategy: 'Contract Optimization',
              savings: 'Recover $50K-500K annually',
              description: 'Automated contract repricing identifies underpayments and ensures accurate reimbursement per negotiated rates.'
            },
            {
              strategy: 'Compliance Assurance',
              savings: 'Avoid $10K-1M+ in penalties',
              description: 'Continuous monitoring prevents costly compliance violations, audits, and payer recoupments.'
            }
          ].map((item, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1, duration: 0.6 }}
              style={{
                padding: '28px',
                backgroundColor: '#f9fafb',
                borderRadius: '12px',
                border: '2px solid #e5e7eb'
              }}
            >
              <h4 style={{
                fontSize: '20px',
                fontWeight: '700',
                color: '#001F2B',
                marginBottom: '8px'
              }}>
                {item.strategy}
              </h4>
              <div style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#F26522',
                marginBottom: '12px'
              }}>
                {item.savings}
              </div>
              <p style={{
                fontSize: '15px',
                color: '#555',
                lineHeight: '1.6'
              }}>
                {item.description}
              </p>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* MEDICAL BILLING OUTSOURCING COMPANIES COMPARISON */}
      <div style={{ padding: '80px 5%', backgroundColor: '#f7f9fb' }}>
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          style={{
            textAlign: 'center',
            fontSize: 'clamp(28px, 4vw, 42px)',
            fontWeight: '800',
            color: '#001F2B',
            marginBottom: '24px'
          }}
        >
          Why We're Among the Best Revenue Cycle Management Companies
        </motion.h2>
        <p style={{
          textAlign: 'center',
          fontSize: '18px',
          color: '#555',
          maxWidth: '800px',
          margin: '0 auto 60px',
          lineHeight: 1.7
        }}>
          Comparing medical billing outsourcing companies? Here's how MDeRCM stands out from traditional revenue cycle management services providers.
        </p>

        <div style={{
          maxWidth: '1100px',
          margin: '0 auto',
          backgroundColor: '#ffffff',
          borderRadius: '16px',
          padding: '40px',
          boxShadow: '0 8px 24px rgba(0,0,0,0.08)'
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '2fr 1fr 1fr',
            gap: '20px',
            fontSize: '15px'
          }}>
            {/* Header Row */}
            <div style={{ fontWeight: '700', color: '#001F2B', fontSize: '16px', padding: '12px' }}>
              Feature
            </div>
            <div style={{ fontWeight: '700', color: '#F26522', fontSize: '16px', padding: '12px', textAlign: 'center' }}>
              MDeRCM
            </div>
            <div style={{ fontWeight: '700', color: '#666', fontSize: '16px', padding: '12px', textAlign: 'center' }}>
              Traditional RCM
            </div>

            {/* Comparison Rows */}
            {[
              ['First-Pass Claim Rate', '98%', '85-90%'],
              ['AR Days', '25-30 days', '45-60 days'],
              ['Denial Rate', '2-3%', '8-12%'],
              ['Staff Reduction', '40-50%', '10-15%'],
              ['Setup Time', '2-4 weeks', '3-6 months'],
              ['Real-Time Analytics', '✓ Yes', '✗ Limited'],
              ['24/7 Automation', '✓ Yes', '✗ No'],
              ['AI-Powered', '✓ Advanced', '✗ Basic'],
              ['Cost Savings', '30-50%', '10-20%'],
              ['Scalability', '✓ Instant', '✗ Slow']
            ].map((row, i) => (
              <React.Fragment key={i}>
                <div style={{ 
                  padding: '16px 12px', 
                  borderBottom: i < 9 ? '1px solid #e5e7eb' : 'none',
                  color: '#333'
                }}>
                  {row[0]}
                </div>
                <div style={{ 
                  padding: '16px 12px', 
                  borderBottom: i < 9 ? '1px solid #e5e7eb' : 'none',
                  textAlign: 'center',
                  fontWeight: '600',
                  color: '#F26522'
                }}>
                  {row[1]}
                </div>
                <div style={{ 
                  padding: '16px 12px', 
                  borderBottom: i < 9 ? '1px solid #e5e7eb' : 'none',
                  textAlign: 'center',
                  color: '#666'
                }}>
                  {row[2]}
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      {/* CASE STUDIES / RESULTS SECTION */}
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={fadeIn}
        style={{ padding: '80px 5%', backgroundColor: '#ffffff' }}
      >
        <h2 style={{
          textAlign: 'center',
          fontSize: 'clamp(28px, 4vw, 42px)',
          fontWeight: '700',
          color: '#001F2B',
          marginBottom: '20px'
        }}>
          Real Results from Our Revenue Cycle Management Services USA
        </h2>
        <p style={{
          textAlign: 'center',
          fontSize: '18px',
          color: '#555',
          maxWidth: '800px',
          margin: '0 auto 60px',
          lineHeight: 1.7
        }}>
          See how our healthcare revenue cycle management services have transformed operations for providers across the United States.
        </p>

        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          gap: '32px'
        }}>
          {[
            {
              specialty: 'Multi-Specialty Hospital (250 beds)',
              challenge: 'High denial rates and slow AR collections',
              results: [
                'Reduced AR days from 62 to 28 days',
                'Improved clean claim rate to 97%',
                'Recovered $2.3M in previously denied claims',
                'Cut RCM staff costs by 45%'
              ]
            },
            {
              specialty: 'Primary Care Network (12 locations)',
              challenge: 'Manual processes causing billing delays',
              results: [
                'Automated 85% of verification tasks',
                'Increased monthly collections by 38%',
                'Reduced claim denials by 70%',
                'Saved $180K annually in staffing costs'
              ]
            },
            {
              specialty: 'Orthopedic Surgery Practice',
              challenge: 'Prior authorization bottlenecks',
              results: [
                'Reduced auth turnaround from 7 to 2 days',
                'Increased surgical case volume by 22%',
                'Improved patient satisfaction scores',
                'Eliminated 3 FTE positions'
              ]
            }
          ].map((caseStudy, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.15, duration: 0.6 }}
              style={{
                width: '100%',
                maxWidth: '380px',
                backgroundColor: '#f9fafb',
                padding: '32px',
                borderRadius: '16px',
                border: '2px solid #F26522'
              }}
            >
              <h4 style={{
                fontSize: '20px',
                fontWeight: '700',
                color: '#001F2B',
                marginBottom: '8px'
              }}>
                {caseStudy.specialty}
              </h4>
              <p style={{
                fontSize: '15px',
                color: '#F26522',
                fontWeight: '600',
                marginBottom: '20px'
              }}>
                {caseStudy.challenge}
              </p>
              <div style={{ marginTop: '16px' }}>
                <strong style={{ color: '#001F2B', display: 'block', marginBottom: '12px' }}>
                  Results Achieved:
                </strong>
                <ul style={{ 
                  margin: 0, 
                  paddingLeft: '20px',
                  listStyleType: 'none'
                }}>
                  {caseStudy.results.map((result, j) => (
                    <li key={j} style={{ 
                      fontSize: '15px', 
                      color: '#555', 
                      marginBottom: '8px',
                      lineHeight: '1.5',
                      position: 'relative',
                      paddingLeft: '24px'
                    }}>
                      <span style={{
                        position: 'absolute',
                        left: 0,
                        color: '#F26522',
                        fontWeight: '700'
                      }}>✓</span>
                      {result}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* RCM TECHNOLOGY STACK */}
      <div style={{ padding: '80px 5%', backgroundColor: '#001F2B', color: '#ffffff' }}>
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          style={{
            textAlign: 'center',
            fontSize: 'clamp(28px, 4vw, 42px)',
            fontWeight: '800',
            marginBottom: '24px'
          }}
        >
          Advanced Technology Behind Our Medical Billing Outsourcing Services
        </motion.h2>
        <p style={{
          textAlign: 'center',
          fontSize: '18px',
          color: '#cccccc',
          maxWidth: '800px',
          margin: '0 auto 60px',
          lineHeight: 1.7
        }}>
          Our revenue cycle management services companies leverage cutting-edge AI and automation technology to deliver superior results.
        </p>

        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          gap: '24px',
          maxWidth: '1200px',
          margin: '0 auto'
        }}>
          {[
            {
              tech: 'AI-Powered Claims Intelligence',
              description: 'Machine learning algorithms analyze millions of claims to predict denials and optimize submissions.'
            },
            {
              tech: 'Natural Language Processing',
              description: 'NLP technology extracts clinical data from medical records for accurate coding and documentation.'
            },
            {
              tech: 'Robotic Process Automation',
              description: 'RPA bots handle repetitive tasks like eligibility checks, payment posting, and EOB processing 24/7.'
            },
            {
              tech: 'Predictive Analytics',
              description: 'Advanced analytics forecast payment patterns, identify at-risk accounts, and optimize collection strategies.'
            },
            {
              tech: 'Real-Time Integration',
              description: 'Seamless integration with 200+ EHR/PM systems including Epic, Cerner, Athenahealth, and eClinicalWorks.'
            },
            {
              tech: 'Intelligent Workflow Automation',
              description: 'Smart routing and prioritization ensure claims flow efficiently through the revenue cycle.'
            }
          ].map((item, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1, duration: 0.6 }}
              style={{
                width: '100%',
                maxWidth: '360px',
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                padding: '28px',
                borderRadius: '12px',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}
            >
              <h4 style={{
                fontSize: '20px',
                fontWeight: '700',
                color: '#F26522',
                marginBottom: '12px'
              }}>
                {item.tech}
              </h4>
              <p style={{
                fontSize: '15px',
                color: '#cccccc',
                lineHeight: '1.6'
              }}>
                {item.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>

      {/* FAQ SECTION */}
      <div style={{ padding: '80px 5%', backgroundColor: '#ffffff' }}>
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          style={{
            textAlign: 'center',
            fontSize: 'clamp(28px, 4vw, 42px)',
            fontWeight: '700',
            color: '#001F2B',
            marginBottom: '60px'
          }}
        >
          Frequently Asked Questions About Our Healthcare RCM Services
        </motion.h2>

        <div style={{
          maxWidth: '900px',
          margin: '0 auto'
        }}>
          {[
            {
              question: 'What makes MDeRCM different from other medical billing outsourcing companies?',
              answer: 'MDeRCM combines AI-powered automation with expert RCM oversight to deliver superior results. Unlike traditional revenue cycle management services companies, we automate 80% of routine tasks, achieve 98% first-pass claim rates, and reduce operational costs by 30-50%. Our healthcare revenue cycle management services include real-time analytics, 24/7 processing, and seamless integration with your existing systems.'
            },
            {
              question: 'How long does it take to implement your outsourced revenue cycle management services?',
              answer: 'Our implementation typically takes 2-4 weeks, significantly faster than the 3-6 months required by traditional medical billing outsourcing services. We provide white-glove onboarding, EHR integration, staff training, and parallel processing to ensure a smooth transition without disrupting your operations.'
            },
            {
              question: 'What healthcare cost reduction strategies do you implement?',
              answer: 'Our healthcare cost reduction consulting includes: AI automation (40-50% labor savings), denial prevention ($25-50 saved per claim), AR optimization (35% lower collection costs), clean claim improvement (15-25% revenue increase), contract repricing (recover $50K-500K annually), and compliance assurance (avoid costly penalties). These proven strategies deliver measurable ROI within 90 days.'
            },
            {
              question: 'Do you provide hospital revenue cycle management services?',
              answer: 'Yes, we specialize in revenue cycle management services for hospitals of all sizes. Our hospital RCM solutions handle complex multi-specialty billing, emergency department claims, inpatient/outpatient services, and high claim volumes. We work with facilities ranging from 50-bed community hospitals to 500+ bed academic medical centers across the USA.'
            },
            {
              question: 'How do I know if I need revenue cycle management consulting services?',
              answer: 'Consider our revenue cycle management consulting services if you experience: AR days over 40, clean claim rates below 90%, denial rates above 5%, high staff turnover, delayed reimbursements, compliance concerns, or limited visibility into RCM performance. We provide free assessments to identify improvement opportunities and quantify potential savings.'
            },
            {
              question: 'What specialties do your medical revenue cycle management services support?',
              answer: 'Our medical revenue cycle management services support all specialties including: primary care, cardiology, orthopedics, OB/GYN, dermatology, mental health, dental, physical therapy, urgent care, and multi-specialty practices. We also provide specialized hospital revenue cycle management for facilities with complex billing requirements.'
            },
            {
              question: 'How do you ensure compliance with healthcare regulations?',
              answer: 'Our revenue cycle management services companies maintain full HIPAA compliance, follow CMS guidelines, and stay current with payer-specific requirements. We provide continuous credentialing monitoring, regulatory updates, audit support, and comprehensive documentation. Our AI systems are trained on current coding standards (ICD-10, CPT, HCPCS) and automatically flag compliance issues.'
            },
            {
              question: 'What results can I expect from your medical billing outsourcing services?',
              answer: 'Typical results from our medical billing outsourcing services include: 98% first-pass claim rates, 25-30 day AR cycles, 2-3% denial rates, 30-50% cost savings, 15-25% revenue increase, and 40% staff reduction. Most clients see measurable improvements within 60-90 days, with full optimization achieved within 6 months.'
            }
          ].map((faq, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1, duration: 0.5 }}
              style={{
                marginBottom: '24px',
                padding: '28px',
                backgroundColor: '#f9fafb',
                borderRadius: '12px',
                border: '1px solid #e5e7eb'
              }}
            >
              <h3 style={{
                fontSize: '19px',
                fontWeight: '700',
                color: '#001F2B',
                marginBottom: '12px'
              }}>
                {faq.question}
              </h3>
              <p style={{
                fontSize: '16px',
                color: '#555',
                lineHeight: '1.7',
                margin: 0
              }}>
                {faq.answer}
              </p>
            </motion.div>
          ))}
        </div>
      </div>

      {/* FINAL CTA SECTION */}
      <div style={{ 
        padding: '80px 5%', 
        backgroundColor: '#F26522',
        color: '#ffffff',
        textAlign: 'center'
      }}>
        <motion.h2
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          style={{
            fontSize: 'clamp(28px, 4vw, 42px)',
            fontWeight: '800',
            marginBottom: '24px',
            lineHeight: 1.3
          }}
        >
          Ready to Transform Your Revenue Cycle?
        </motion.h2>
        <p style={{
          fontSize: '20px',
          maxWidth: '800px',
          margin: '0 auto 40px',
          lineHeight: 1.7
        }}>
          Join hundreds of healthcare providers who trust MDeRCM for comprehensive healthcare revenue cycle management services. Experience the difference that AI-powered automation and expert oversight can make.
        </p>

        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          gap: '20px',
          marginBottom: '40px'
        }}>
          <motion.a
            href="/free-trial"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            style={{
              backgroundColor: '#ffffff',
              color: '#F26522',
              padding: '18px 40px',
              borderRadius: '8px',
              fontSize: '18px',
              fontWeight: '700',
              textDecoration: 'none',
              display: 'inline-block',
              boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
            }}
          >
            Start Your Free Trial
          </motion.a>
          <motion.a
            href="/company"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            style={{
              backgroundColor: 'transparent',
              color: '#ffffff',
              padding: '18px 40px',
              borderRadius: '8px',
              fontSize: '18px',
              fontWeight: '700',
              textDecoration: 'none',
              display: 'inline-block',
              border: '2px solid #ffffff'
            }}
          >
            Schedule Consultation
          </motion.a>
        </div>

        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          gap: '40px',
          marginTop: '60px'
        }}>
          {[
            '500+ Healthcare Providers',
            '$50M+ Revenue Recovered',
            '98% Client Satisfaction'
          ].map((stat, i) => (
            <div key={i} style={{ textAlign: 'center' }}>
              <div style={{ 
                fontSize: '18px', 
                fontWeight: '600',
                opacity: 0.95
              }}>
                {stat}
              </div>
            </div>
          ))}
        </div>
      </div>

      <ConsultationCTA />
    </>
  );
};

export default HealthcareAI;